// Birtvy Open — the experiment's doc model. A Birtvy-shaped canvas whose slides are OPEN-SLIDE-CLASS
// artifacts: each slide is one complete, self-contained HTML document (its own styles, fonts, keyframes,
// optional tiny scripts) rendered live in a sandboxed iframe. No baked elements, no chat agents, no
// backend: this environment exists to feel what unconstrained generated slides are like INSIDE our
// canvas, per the open-slide study (Deck AI/OPEN_SLIDE_STUDY.md).
//
// Deliberate deviations from the product canvas, all scoped to this route:
// - Slides are LIVE iframes at rest. The canvas-performance skill forbids that on /canvas for 100+ slide
//   decks; this sandbox holds a handful, and the point is watching the content run.
// - Persistence is localStorage only ("frontend, not backend" was the brief).
// - The iframe carries sandbox="allow-scripts" and NOT allow-same-origin: opaque origin, no cookies, no
//   app DOM access. This is the exact isolation shape the study recommends if raw HTML ever ships for
//   real, so the experiment doubles as that testbed.
//
// Pure module: types, constants, layout, the html wrapper, and localStorage round-trip. No React here.

/** The product slide size — same fixed-canvas discipline open-slide uses, same numbers Birtvy uses. */
export const SLIDE_W = 1920
export const SLIDE_H = 1080
/** Gap between slides in the single-row layout. One row keeps present order = left-to-right, no grid logic. */
export const SLIDE_GAP = 200
export const SLIDE_PITCH = SLIDE_W + SLIDE_GAP

export interface BirtvyOpenSlide {
  id: number
  x: number
  y: number
  /** A COMPLETE self-contained HTML document (doctype to closing html). Everything the slide needs lives
   *  inside: styles, @keyframes, webfont links, inline scripts. Rendered via iframe srcdoc, so it may use
   *  any CSS or JS without touching the app. Build fragments with {@link wrapSlideHtml}. */
  html: string
  /** Shown in chrome and the present progress line. Optional; untitled slides render as "Slide n". */
  title?: string
  /** The last prompt typed into the slide's input. Generation is not wired yet (next step of the
   *  experiment) — kept so a typed idea survives reload instead of vanishing. */
  prompt?: string
}

export interface BirtvyOpenDeck {
  slides: BirtvyOpenSlide[]
  /** Next slide id. Monotonic, never reused, so React keys and iframe remounts stay honest. */
  nextId: number
}

/** Present order is reading order: left to right along the row. */
export function presentOrder(slides: readonly BirtvyOpenSlide[]): BirtvyOpenSlide[] {
  return [...slides].sort((a, b) => a.x - b.x || a.id - b.id)
}

/** X for the slot after the current rightmost slide. */
export function nextSlideX(slides: readonly BirtvyOpenSlide[]): number {
  if (slides.length === 0) return 0
  return Math.max(...slides.map((s) => s.x)) + SLIDE_PITCH
}

/**
 * Wraps slide BODY markup (+ its <style>/<script>) into the full standalone document the iframe renders.
 * The shell pins the document to exactly 1920x1080 with no margins and no scrolling — the same fixed
 * canvas the authoring contract assumes — so slide authors write absolute pixels and they are correct.
 * `head` is for webfont <link>s and anything else that belongs in <head>.
 */
export function wrapSlideHtml(body: string, head = ''): string {
  return [
    '<!doctype html>',
    '<html>',
    '<head>',
    '<meta charset="utf-8">',
    `<style>html,body{margin:0;padding:0;width:${SLIDE_W}px;height:${SLIDE_H}px;overflow:hidden;background:#ffffff}*{box-sizing:border-box}</style>`,
    head,
    '</head>',
    '<body>',
    body,
    '</body>',
    '</html>',
  ].join('\n')
}

/** A fresh empty slide document — plain white, nothing else. What the + buttons insert. */
export function blankSlideHtml(): string {
  return wrapSlideHtml('')
}

// ── Persistence (localStorage) ────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'birtvy-open:deck:v1'

/** Parses defensively: any malformed blob falls back rather than taking the route down. */
export function loadDeck(fallback: BirtvyOpenDeck): BirtvyOpenDeck {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<BirtvyOpenDeck>
    if (!Array.isArray(parsed.slides)) return fallback
    const slides = parsed.slides.filter(
      (s): s is BirtvyOpenSlide =>
        !!s && typeof s.id === 'number' && typeof s.x === 'number' && typeof s.y === 'number' && typeof s.html === 'string',
    )
    if (slides.length === 0) return fallback
    const maxId = Math.max(...slides.map((s) => s.id))
    return { slides, nextId: Math.max(typeof parsed.nextId === 'number' ? parsed.nextId : 0, maxId + 1) }
  } catch {
    return fallback
  }
}

export function saveDeck(deck: BirtvyOpenDeck): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(deck))
  } catch {
    // Quota or private mode — the experiment keeps working, it just does not survive reload.
  }
}

/** Wipes the stored deck so the next load reseeds. Wired to the chrome's reset control. */
export function resetDeck(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do.
  }
}
